import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, Eye, EyeOff, Trash2, Monitor } from "lucide-react";
import SessionActivityDialog from "@/components/SessionActivityDialog";

const ROLES = [
  { v: "admin", label: "Administrator" },
  { v: "business_development", label: "Business Development" },
  { v: "accounts", label: "Accounts" },
];

const emptyForm = { email: "", name: "", display_name: "", password: "", role: "business_development", phone: "", country: "", notes: "" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sessionUser, setSessionUser] = useState(null);

  const load = async () => { const { data } = await api.get("/api/users"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/api/users", form);
      toast.success("User created");
      setOpen(false); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...emptyForm, ...u, password: "" });
    setNewPwd(""); setConfirmPwd(""); setShowPwd(false);
  };

  const saveEdit = async () => {
    try {
      const patch = { ...form };
      delete patch.password;  // never sent from Edit; use Set New Password below
      delete patch.id;
      // strip fields that aren't user-updatable
      delete patch.created_at; delete patch.updated_at;
      await api.patch(`/api/users/${editing.id}`, patch);
      toast.success("User updated");
      setEditing(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggle = async (u) => {
    await api.patch(`/api/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  const removeUser = async (u) => {
    // Deleting a BD does NOT delete any of their associated business data —
    // clients, projects, incentive rules and payments are all preserved with
    // a historical name snapshot so reports keep working.
    if (!window.confirm(`Remove user "${u.name}"?\n\nAll associated data (clients, projects, invoices, payments, incentive rules) will be preserved — only the login is removed.`)) return;
    try {
      const { data } = await api.delete(`/api/users/${u.id}`);
      if (data.removed) {
        toast.success(`Deleted "${u.name}" — associated data preserved with their name snapshot.`);
      } else {
        const refs = data.references || {};
        const summary = Object.entries(refs)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
          .join(", ");
        const proceed = window.confirm(
          `"${u.name}" is referenced by ${summary}.\n\n` +
          `They have been DEACTIVATED for now.\n\n` +
          `Force-remove the login anyway? Their ${summary} will REMAIN in the database with their name preserved — nothing is deleted.`
        );
        if (proceed) {
          await api.delete(`/api/users/${u.id}?hard=true`);
          toast.success(`Removed "${u.name}" — their historical data is preserved.`);
        } else {
          toast.message("User deactivated instead of removed.");
        }
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove user");
    }
  };

  const setUserPassword = async () => {
    if (!editing) return;
    const pwd = (newPwd || "").trim();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters");
    if (pwd !== (confirmPwd || "").trim()) return toast.error("Passwords do not match");
    if (!window.confirm(`Set a new password for ${editing.name}? They will sign in with this password directly — no forced change.`)) return;
    setSavingPwd(true);
    try {
      await api.post(`/api/users/${editing.id}/reset-password`, { new_password: pwd });
      toast.success(`New password set for ${editing.name} — share it securely.`);
      setNewPwd(""); setConfirmPwd(""); setShowPwd(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to set password"); }
    finally { setSavingPwd(false); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Users & Roles</h1>
          <p className="text-sm text-slate-500 mt-1">Manage team members and their permissions. Admins can set a new permanent password for any user — no temporary passwords, no forced resets.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-btn" className="bg-brand hover:bg-brand-hover"><Plus className="w-4 h-4 mr-2" />Add User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5"><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Initial Password *</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 chars — share securely" autoComplete="new-password" data-testid="user-password" /></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-brand hover:bg-brand-hover" onClick={create} data-testid="save-user-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="overflow-x-auto">
          <table className="data-table w-full">
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Last sign-in</th><th></th></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.name}{u.is_historical ? <span className="ml-1.5 text-[9px] uppercase text-slate-500 bg-slate-100 rounded px-1">historical</span> : null}</td>
                  <td className="text-slate-600 text-xs">{u.email}</td>
                  <td className="text-slate-600 text-xs">{u.phone || "—"}</td>
                  <td>{u.role?.replace(/_/g, " ")}</td>
                  <td><StatusBadge status={u.is_active ? "confirmed" : "cancelled"}>{u.is_active ? "active" : "inactive"}</StatusBadge></td>
                  <td className="text-xs text-slate-600 whitespace-nowrap" data-testid={`user-lastlogin-${u.id}`}>
                    {u.last_login_at ? (
                      <div className="flex flex-col leading-tight">
                        <span className="text-slate-800">
                          {new Date(u.last_login_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                        <span className="font-mono text-[10px] text-slate-500">
                          {u.last_login_ip || "—"}
                          {u.active_session_count > 0 ? (
                            <span className="ml-1.5 text-emerald-700">· {u.active_session_count} active</span>
                          ) : null}
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-400">Never</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setSessionUser(u)} data-testid={`sessions-user-${u.id}`} title="Session activity">
                        <Monitor className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}>{u.is_active ? "Deactivate" : "Activate"}</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeUser(u)}
                        className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        data-testid={`delete-user-${u.id}`}
                        title="Remove this user from the directory"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog with Administrative Controls */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setNewPwd(""); setConfirmPwd(""); setShowPwd(false); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit User · {editing?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["name", "Full Name *"],
              ["display_name", "Display Name"],
              ["email", "Email *"],
              ["phone", "Phone"],
              ["country", "Country / Region"],
            ].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-user-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {/* Profile picture — admin can set/replace/remove any user's avatar. */}
          <div className="border-t border-slate-200 pt-4 mt-2 space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Profile picture</div>
            <div className="flex items-center gap-4">
              <img
                src={form.avatar_url || "/tecticonism-wordmark-sm.png"}
                alt=""
                className={`w-16 h-16 rounded-full border border-slate-200 ${form.avatar_url ? "object-cover" : "object-contain p-2 bg-slate-50"}`}
                data-testid="edit-user-avatar-preview"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="text-xs"
                    data-testid="edit-user-avatar-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith("image/")) { toast.error("Choose an image"); return; }
                      // Reuse the same 300×300 JPEG resize as Settings.
                      const bitmap = await createImageBitmap(file);
                      const c = document.createElement("canvas");
                      c.width = 300; c.height = 300;
                      const ctx = c.getContext("2d");
                      const short = Math.min(bitmap.width, bitmap.height);
                      const sx = (bitmap.width - short) / 2;
                      const sy = (bitmap.height - short) / 2;
                      ctx.drawImage(bitmap, sx, sy, short, short, 0, 0, 300, 300);
                      setForm({ ...form, avatar_url: c.toDataURL("image/jpeg", 0.86) });
                      e.target.value = "";
                    }}
                  />
                  {form.avatar_url && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, avatar_url: "" })} data-testid="edit-user-avatar-remove" className="text-rose-600">
                      Remove
                    </Button>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">Resized to 300×300 JPEG on your device. Max 600 KB after resize.</div>
              </div>
            </div>
          </div>

          {/* Administrative Controls — set a new password of admin's choice */}
          <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-rose-600 font-medium flex items-center gap-1.5">
                <KeyRound className="w-3 h-3" />
                Set New Password
              </div>
              <div className="text-[11px] text-slate-500">
                Admin-only · No temporary password · User signs in with this directly
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">New Password (min 8 chars)</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="Choose a strong password"
                    autoComplete="new-password"
                    className="pr-9"
                    data-testid="admin-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    title={showPwd ? "Hide" : "Show"}
                    data-testid="toggle-show-password"
                  >
                    {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirm Password</Label>
                <Input
                  type={showPwd ? "text" : "password"}
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  placeholder="Re-type the same password"
                  autoComplete="new-password"
                  data-testid="admin-confirm-password"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={setUserPassword}
                disabled={savingPwd || !newPwd || !confirmPwd}
                className="bg-rose-700 hover:bg-rose-800"
                data-testid="set-password-btn"
              >
                <KeyRound className="w-3 h-3 mr-1" />
                {savingPwd ? "Saving…" : "Set new password"}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500">
              The user's existing password will be replaced immediately. Share the new password with them through a secure channel — it is <b>not</b> stored in plain text or emailed.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setNewPwd(""); setConfirmPwd(""); setShowPwd(false); setForm(emptyForm); }}>Close</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEdit} data-testid="save-edit-user">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SessionActivityDialog
        user={sessionUser}
        open={!!sessionUser}
        onOpenChange={(v) => !v && setSessionUser(null)}
        onRevoked={load}
      />
    </div>
  );
}
